import React, { useCallback, useEffect, useRef, useState } from "react";
import { Col, Icon, Row } from "antd";
import { useApolloClient } from "@apollo/react-hooks";
import { useHistory, useParams } from "react-router-dom";

import AttachmentActions from "components/attachments/attachment-actions";
import Icons from "components/icons";
import { ErrorBlock, Loader } from "components/result";
import { Localize } from "components/service";
import { ContainerWrap } from "components/wraps";
import {
    getAttachmentErrorKey,
    getAttachmentFileName,
    getFreshAttachment,
    isPdfAttachment
} from "utils/attachment";
import {
    isAttachmentAuthenticationError,
    redirectAttachmentToLogin
} from "utils/attachment-auth";

import "./attachment-page.css";

const AttachmentPage = () => {
    const { id } = useParams();
    const history = useHistory();
    const client = useApolloClient();
    const requestVersion = useRef(0);
    const pendingRequest = useRef(null);
    const [state, setState] = useState({ id: null, loading: true });

    const loadAttachment = useCallback(async () => {
        if (pendingRequest.current &&
            pendingRequest.current.id === id &&
            pendingRequest.current.version === requestVersion.current) return;

        const version = ++requestVersion.current;
        pendingRequest.current = { id, version };
        setState({ id, loading: true });

        try {
            const attachment = await getFreshAttachment(client, id);
            if (version !== requestVersion.current) return;
            setState({ id, loading: false, attachment });
        } catch (error) {
            if (version !== requestVersion.current) return;
            if (isAttachmentAuthenticationError(error)) {
                await redirectAttachmentToLogin(client, history, id);
                return;
            }
            setState({ id, loading: false, error: getAttachmentErrorKey(error) });
        } finally {
            if (pendingRequest.current && pendingRequest.current.version === version) {
                pendingRequest.current = null;
            }
        }
    }, [client, history, id]);

    useEffect(() => {
        loadAttachment();
        return () => {
            // Pending queries must not render another file or redirect after leaving.
            requestVersion.current += 1;
        };
    }, [loadAttachment]);

    const loading = state.id !== id || state.loading;
    const attachment = !loading && state.attachment;
    const error = !loading && state.error;
    const pdf = attachment && isPdfAttachment(attachment);
    // The preview URL lives only in this page's transient state; actions fetch their own.
    const metadata = attachment && {
        id: attachment.id,
        name: attachment.name,
        title: attachment.title,
        extension: attachment.extension
    };

    return (
        <ContainerWrap className="container-margin">
            <Row type="flex" justify="center">
                <Col xs={24} lg={18} xl={16} xxl={14}>
                    <section className="attachment-page" aria-busy={loading}>
                        <div className="attachment-page__header">
                            <div className="attachment-page__icon company-icon-color" aria-hidden="true">
                                <Icons.File type={pdf ? "pdf" : undefined} />
                            </div>
                            <div className="attachment-page__heading">
                                <h1 className="attachment-page__label site-sub-text-color"><Localize>ATTACHMENTS.Title</Localize></h1>
                                {attachment && (
                                    <h2 className="attachment-page__name site-text-color">
                                        {attachment.title || getAttachmentFileName(attachment)}
                                    </h2>
                                )}
                            </div>
                        </div>
                        <div className="attachment-page__body">
                            {loading && <Loader type="block" />}
                            {error && (
                                <ErrorBlock
                                    title={<Localize>ATTACHMENTS.Title</Localize>}
                                    subtitle={<Localize>{error}</Localize>}
                                    action={loadAttachment}
                                    buttonText={<Localize>ATTACHMENTS.Button_Retry</Localize>}
                                />
                            )}
                            {attachment && (
                                <>
                                    <div className="attachment-page__actions">
                                        <AttachmentActions attachment={metadata} download>
                                            {pdf && (
                                                <button type="button" className="attachment-action-button button-bg-color" onClick={loadAttachment}>
                                                    <Icon type="reload" />
                                                    <Localize wrap>ATTACHMENTS.Button_RefreshPreview</Localize>
                                                </button>
                                            )}
                                        </AttachmentActions>
                                    </div>
                                    {pdf && (
                                        <>
                                            <p className="attachment-page__help site-sub-text-color">
                                                <Localize>ATTACHMENTS.Text_PreviewHelp</Localize>
                                            </p>
                                            <iframe
                                                className="attachment-page__preview"
                                                title={getAttachmentFileName(attachment)}
                                                src={attachment.display_url}
                                                referrerPolicy="no-referrer"
                                            />
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                    </section>
                </Col>
            </Row>
        </ContainerWrap>
    );
};

export default AttachmentPage;
